/**
 * Report-text assembly — the `TD - …` / `UM - …` serialisation.
 *
 * Authority: `docs/specs/rulings-2026-08-11-open-questions.md` §E (ruled = A, evening of
 * 11 August 2026) and `docs/specs/control-centre/05-reporting-marking.md` §B5a.
 *
 * **The ruling, in full.** The TD's closing remarks **auto-insert into the owning UM's
 * report draft**: the TD's block sits at the **top**, prefixed `TD - `; the UM's own
 * narrative follows **below**, prefixed `UM - ` — the existing TMS convention, where the UM
 * opens their part with `UM-` and writes beneath the TD's part already populated at the top
 * of the TMS text box. Option B (a consolidated message the UM pastes) is **rejected**,
 * because it reintroduces the copy step option A exists to remove.
 *
 * **The column stores RAW remarks.** `report.td_closing_remarks` holds the TD's text without
 * any prefix (`10-data-model.md` §6, migration 006); serialisation happens here, at render
 * or push time, and only here. That is why the normal path always adds the prefix — the
 * already-prefixed guard below is a belt-and-braces measure against a TD who typed the
 * convention out by hand, never the expected route.
 *
 * British English throughout, per the standing copy rule.
 */
/** The TD's prefix, verbatim from the ruling — hyphen-minus, one space either side. */
export const TD_REPORT_PREFIX = "TD - ";
/** The UM's prefix, verbatim from the ruling. */
export const UM_REPORT_PREFIX = "UM - ";
/**
 * A part that is already serialised. Tolerant of the TMS spelling `UM-` (no space) as well
 * as `UM - `, and of any leading whitespace, so a hand-typed prefix is never doubled.
 */
const ALREADY_PREFIXED = {
    TD: /^TD\s*-\s*/i,
    UM: /^UM\s*-\s*/i,
};
/** Trim to a definite string; blank and whitespace-only both collapse to `""`. */
function normalise(value) {
    return typeof value === "string" ? value.trim() : "";
}
/** Prefix a non-empty part, unless it already carries that prefix. */
function prefixed(body, prefix, already) {
    if (body === "")
        return "";
    return already.test(body) ? body.replace(already, prefix) : `${prefix}${body}`;
}
/**
 * Serialise a report exactly as ruling §E requires:
 *
 * ```
 * TD - <the TD's closing remarks>
 *
 * UM - <the UM's narrative>
 * ```
 *
 * * **Both parts** — TD block first, one blank line, UM block beneath.
 * * **TD only** — the TD block alone. The UM pastes their part beneath it in TMS.
 * * **UM only** — the UM block alone; no empty `TD - ` line is emitted.
 * * **Neither** — the empty string, never a lone prefix.
 *
 * Internal line breaks are preserved as written; only the outer edges are trimmed.
 */
export function assembleReportText(input) {
    const td = prefixed(normalise(input.tdClosingRemarks), TD_REPORT_PREFIX, ALREADY_PREFIXED.TD);
    const um = prefixed(normalise(input.umBody), UM_REPORT_PREFIX, ALREADY_PREFIXED.UM);
    if (td !== "" && um !== "")
        return `${td}\n\n${um}`;
    return td !== "" ? td : um;
}
//# sourceMappingURL=report-assembly.js.map